import type { Moment } from 'moment'
import moment from 'moment'

export type ExperienceDateDisplayPreset = 'today' | 'tomorrow' | 'weekend'

/**
 * Upcoming Saturday–Sunday range (start Saturday 00:00, end Sunday startOf day).
 * Shared by quick chips and date field display matching.
 */
export function getThisWeekendRange(): { start: Moment, end: Moment } {
  const now = moment()
  const day = now.day() // 0 Sun … 6 Sat
  let saturday: Moment
  if (day === 6) {
    saturday = now.clone().startOf('day')
  } else if (day === 0) {
    // Sunday: next Saturday is in 6 days
    saturday = now.clone().add(6, 'days').startOf('day')
  } else {
    saturday = now.clone().day(6).startOf('day')
    if (saturday.isBefore(now, 'day')) {
      saturday.add(1, 'week')
    }
  }
  const sunday = saturday.clone().add(1, 'day').startOf('day')
  return { start: saturday, end: sunday }
}

/**
 * Formats date range as "20 - 26 Aug 2026" (or crosses month/year as needed).
 */
export function formatDateRangeLabel(checkIn: Moment, checkOut: Moment): string {
  if (checkIn.isSame(checkOut, 'day')) {
    return checkIn.format('D MMM YYYY')
  }
  const sameMonth = checkIn.isSame(checkOut, 'month')
  const sameYear = checkIn.isSame(checkOut, 'year')
  if (sameMonth && sameYear) {
    return `${checkIn.date()} - ${checkOut.date()} ${checkIn.format('MMM YYYY')}`
  }
  if (sameYear) {
    return `${checkIn.format('D MMM')} - ${checkOut.format('D MMM YYYY')}`
  }
  return `${checkIn.format('D MMM YYYY')} - ${checkOut.format('D MMM YYYY')}`
}

/**
 * Returns a preset when the range matches Today / Tomorrow / This weekend (same logic as quick chips).
 * Otherwise returns null so the caller can show {@link formatDateRangeLabel}.
 */
export function getExperienceDateRangeDisplayPreset(
  checkIn: Moment,
  checkOut: Moment | undefined,
): ExperienceDateDisplayPreset | null {
  const end = checkOut ?? checkIn
  const todayStart = moment().startOf('day')
  const tomorrowStart = moment().add(1, 'day').startOf('day')

  if (checkIn.isSame(todayStart, 'day') && end.isSame(todayStart, 'day')) {
    return 'today'
  }
  if (checkIn.isSame(tomorrowStart, 'day') && end.isSame(tomorrowStart, 'day')) {
    return 'tomorrow'
  }
  const { start: weekendStart, end: weekendEnd } = getThisWeekendRange()
  if (checkIn.isSame(weekendStart, 'day') && end.isSame(weekendEnd, 'day')) {
    return 'weekend'
  }
  return null
}

/**
 * Resolved display for the experiences date field: preset labels (Today, Tomorrow, This weekend)
 * are intended to be rendered via i18n in the component; range uses {@link formatDateRangeLabel}.
 */
export function resolveExperienceDateRangeDisplay(
  checkIn: Moment,
  checkOut: Moment | undefined,
): { preset: ExperienceDateDisplayPreset | null; rangeLabel: string | null } {
  const preset = getExperienceDateRangeDisplayPreset(checkIn, checkOut)
  if (preset) {
    return { preset, rangeLabel: null }
  }
  const end = checkOut ?? checkIn
  return { preset: null, rangeLabel: formatDateRangeLabel(checkIn, end) }
}
